if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.index = 0;
        this.items = [];
        this.pointers = new Map();
        this.interaction = null;
        this.navigationTimer = null;
        this.settleTimer = null;
        this.wheelDistance = 0;
        this.wheelGestureTimer = null;
        this.wheelGestureNavigated = false;
        this.wheelLastDirection = 0;
        this.wheelLastEventTime = 0;
        this.wheelLastMagnitude = 0;
        this.wheelPendingDirection = 0;
        this.trackpadGestureScale = 1;
        this.imageRequestId = 0;
        this.zoom = { scale: 1, x: 0, y: 0 };

        this.handleKeydown = this.handleKeydown.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerEnd = this.handlePointerEnd.bind(this);
        this.handlePointerCancel = this.handlePointerCancel.bind(this);
        this.handleWheel = this.handleWheel.bind(this);
        this.handleGestureStart = this.handleGestureStart.bind(this);
        this.handleGestureChange = this.handleGestureChange.bind(this);
        this.handleGestureEnd = this.handleGestureEnd.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();
        if (this.viewerInitialized) return;

        this.viewerInitialized = true;
        this.dialog = this.querySelector('[role="dialog"]');
        this.header = this.querySelector('[data-product-modal-header]');
        this.viewer = this.querySelector('[data-product-modal-viewer]');
        this.track = this.querySelector('[data-product-modal-track]');
        this.panels = Array.from(this.querySelectorAll('[data-product-modal-panel]'));
        this.count = this.querySelector('[data-product-modal-count]');
        this.previousButton = this.querySelector('[data-product-modal-previous]');
        this.nextButton = this.querySelector('[data-product-modal-next]');
        this.fallback = this.querySelector('[data-product-modal-fallback]');
        this.imageItems = this.parseItems();
        this.items = this.imageItems;

        this.addEventListener('keydown', this.handleKeydown);
        this.dialog?.addEventListener('pointerup', (event) => event.stopPropagation());
        this.viewer?.addEventListener('pointerdown', this.handlePointerDown);
        this.viewer?.addEventListener('pointermove', this.handlePointerMove);
        this.viewer?.addEventListener('pointerup', this.handlePointerEnd);
        this.viewer?.addEventListener('pointercancel', this.handlePointerCancel);
        this.viewer?.addEventListener('wheel', this.handleWheel, { passive: false });
        this.viewer?.addEventListener('gesturestart', this.handleGestureStart, { passive: false });
        this.viewer?.addEventListener('gesturechange', this.handleGestureChange, { passive: false });
        this.viewer?.addEventListener('gestureend', this.handleGestureEnd, { passive: false });
        this.previousButton?.addEventListener('click', () => this.navigate(-1));
        this.nextButton?.addEventListener('click', () => this.navigate(1));
      }

      parseItems() {
        try {
          const items = JSON.parse(this.querySelector('[data-product-modal-items]')?.textContent || '[]');
          return items.map((item) => ({ ...item, id: String(item.id) }));
        } catch (error) {
          console.error('Unable to read product media', error);
          return [];
        }
      }

      show(opener) {
        this.resetViewer();
        this.syncLayeredPreviewItem();
        const mediaId = opener?.getAttribute('data-media-id');
        const itemIndex = this.items.findIndex((item) => item.id === mediaId);

        if (itemIndex === -1) {
          super.show(opener);
          this.showFallbackMedia(mediaId);
          return;
        }

        this.dataset.mode = 'image';
        this.index = itemIndex;
        this.openingSource =
          this.items[itemIndex].type === 'layered-preview'
            ? ''
            : opener?.closest('modal-opener')?.querySelector('img')?.currentSrc || '';
        this.header.hidden = false;
        this.viewer.hidden = false;
        this.fallback.hidden = true;
        this.previousButton?.removeAttribute('hidden');
        this.nextButton?.removeAttribute('hidden');
        this.renderPanels();
        this.openingSource = '';
        super.show(opener);
        this.loadFullSizeImage(itemIndex);
        requestAnimationFrame(() => this.viewer?.focus({ preventScroll: true }));
      }

      syncLayeredPreviewItem() {
        this.items = [...this.imageItems];
        const section = this.closest('.shopify-section') || document;
        const slide = section.querySelector('[data-layered-preview-slide]');
        const source = slide?.querySelector('.layered-preview-canvas');
        if (!slide || slide.hidden || !source) return;

        this.items.splice(Math.min(1, this.items.length), 0, {
          id: 'layered-preview',
          type: 'layered-preview',
          source,
          label: slide.getAttribute('aria-label') || '',
        });
      }

      hide() {
        this.resetViewer();
        super.hide();
      }

      showFallbackMedia(mediaId) {
        this.dataset.mode = 'fallback';
        this.header.hidden = true;
        this.viewer.hidden = true;
        this.previousButton?.setAttribute('hidden', '');
        this.nextButton?.setAttribute('hidden', '');
        this.fallback.hidden = false;

        this.fallback.querySelectorAll('[data-media-id]').forEach((media) => {
          media.classList.toggle('active', media.dataset.mediaId === mediaId);
        });

        this.fallback.querySelector(`[data-media-id="${CSS.escape(mediaId || '')}"] deferred-media`)?.loadContent();
      }

      renderPanels() {
        this.clearLayeredPreviewPanels();
        const panelIndexes = [this.index - 1, this.index, this.index + 1];

        this.panels.forEach((panel, panelIndex) => {
          const itemIndex = panelIndexes[panelIndex];
          const item = this.items[itemIndex];
          const image = panel.querySelector('[data-product-modal-image]');
          const isCurrent = panelIndex === 1 && Boolean(item);

          panel.dataset.itemIndex = item ? String(itemIndex) : '';
          panel.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');

          if (!item) {
            image.hidden = true;
            image.removeAttribute('src');
            image.removeAttribute('width');
            image.removeAttribute('height');
            image.alt = '';
            return;
          }

          if (item.type === 'layered-preview') {
            image.hidden = true;
            image.alt = '';
            const preview = item.source.cloneNode(true);
            preview.removeAttribute('aria-live');
            preview.classList.add('product-media-modal__layered-preview');
            preview.setAttribute('data-product-modal-layered-preview', '');
            preview.setAttribute('data-product-modal-zoom-target', '');
            preview.setAttribute('role', 'img');
            preview.setAttribute('aria-label', isCurrent ? item.label : '');
            panel.append(preview);
            return;
          }

          image.hidden = false;
          image.src = isCurrent && this.openingSource ? this.openingSource : item.src;
          image.width = item.width;
          image.height = item.height;
          image.alt = isCurrent ? item.label : '';
          image.fetchPriority = isCurrent ? 'high' : 'auto';
        });

        this.track.classList.remove('is-animating', 'is-dragging');
        this.track.style.transform = 'translate3d(-100%, 0, 0)';
        this.updateControls();
      }

      loadFullSizeImage(itemIndex) {
        const item = this.items[itemIndex];
        const image = this.currentImage;
        if (!item || item.type === 'layered-preview' || !image || image.src === item.src) return;

        const requestId = ++this.imageRequestId;
        const fullSizeImage = new Image();
        fullSizeImage.decoding = 'async';
        fullSizeImage.src = item.src;

        const ready = fullSizeImage.decode
          ? fullSizeImage.decode()
          : new Promise((resolve, reject) => {
              fullSizeImage.addEventListener('load', resolve, { once: true });
              fullSizeImage.addEventListener('error', reject, { once: true });
            });
        ready.then(() => {
          if (requestId !== this.imageRequestId || !this.hasAttribute('open') || this.index !== itemIndex) return;
          this.currentImage.src = item.src;
        }).catch(() => {});
      }

      updateControls() {
        const item = this.items[this.index];
        if (!item) return;

        this.count.textContent = `${this.index + 1} / ${this.items.length}`;
        const showNavigation = this.items.length > 1;
        if (this.previousButton) {
          this.previousButton.hidden = !showNavigation;
          this.previousButton.disabled = this.index === 0;
        }
        if (this.nextButton) {
          this.nextButton.hidden = !showNavigation;
          this.nextButton.disabled = this.index === this.items.length - 1;
        }
      }

      navigate(direction) {
        if (this.dataset.mode !== 'image' || this.navigationTimer) return;

        if (this.settleTimer) window.clearTimeout(this.settleTimer);
        this.settleTimer = null;

        const nextIndex = this.index + direction;
        if (nextIndex < 0 || nextIndex >= this.items.length) {
          this.settleTrack();
          return;
        }

        this.resetZoom();

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.index = nextIndex;
          this.renderPanels();
          return;
        }

        this.track.classList.remove('is-dragging');
        this.track.classList.add('is-animating');
        requestAnimationFrame(() => {
          this.track.style.transform = direction > 0 ? 'translate3d(-200%, 0, 0)' : 'translate3d(0, 0, 0)';
        });

        this.navigationTimer = window.setTimeout(() => {
          this.navigationTimer = null;
          this.index = nextIndex;
          this.renderPanels();
          const pendingDirection = this.wheelPendingDirection;
          this.wheelPendingDirection = 0;
          if (pendingDirection) requestAnimationFrame(() => this.navigate(pendingDirection));
        }, 280);
      }

      settleTrack() {
        if (this.settleTimer) window.clearTimeout(this.settleTimer);
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.track.classList.remove('is-animating', 'is-dragging');
          this.track.style.transform = 'translate3d(-100%, 0, 0)';
          return;
        }

        this.track.classList.remove('is-dragging');
        this.track.classList.add('is-animating');
        this.track.style.transform = 'translate3d(-100%, 0, 0)';
        this.settleTimer = window.setTimeout(() => {
          this.settleTimer = null;
          this.track?.classList.remove('is-animating');
        }, 280);
      }

      handleKeydown(event) {
        if (!this.hasAttribute('open') || this.dataset.mode !== 'image') return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

        event.preventDefault();
        this.navigate(event.key === 'ArrowRight' ? 1 : -1);
      }

      handlePointerDown(event) {
        if (this.navigationTimer || (event.pointerType === 'mouse' && event.button !== 0)) return;

        if (this.settleTimer) window.clearTimeout(this.settleTimer);
        this.settleTimer = null;
        this.track.classList.remove('is-animating');

        const point = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          target: event.target,
        };
        this.pointers.set(event.pointerId, point);
        this.viewer.setPointerCapture?.(event.pointerId);

        if (this.pointers.size === 2) {
          event.preventDefault();
          this.startPinch();
          return;
        }

        this.interaction = {
          type: this.zoom.scale > 1.01 ? 'pan' : 'swipe',
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          startZoomX: this.zoom.x,
          startZoomY: this.zoom.y,
          edgeOverflowX: 0,
          startedOnImage: Boolean(
            event.target.closest('[data-product-modal-image], [data-product-modal-zoom-target]')
          ),
          moved: false,
        };

        this.track.classList.add('is-dragging');
      }

      handlePointerMove(event) {
        const point = this.pointers.get(event.pointerId);
        if (!point) return;

        point.x = event.clientX;
        point.y = event.clientY;

        if (this.pointers.size >= 2) {
          event.preventDefault();
          if (this.interaction?.type !== 'pinch') this.startPinch();
          this.movePinch();
          return;
        }

        const interaction = this.interaction;
        if (!interaction || interaction.pointerId !== event.pointerId) return;

        const deltaX = event.clientX - interaction.startX;
        const deltaY = event.clientY - interaction.startY;
        if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) interaction.moved = true;

        if (interaction.type === 'pan') {
          event.preventDefault();
          interaction.edgeOverflowX = this.setZoomPosition(
            interaction.startZoomX + deltaX,
            interaction.startZoomY + deltaY
          );
          this.applyZoom();
          return;
        }

        if (interaction.type === 'swipe' && interaction.moved) {
          event.preventDefault();
          let offset = deltaX;
          if ((this.index === 0 && offset > 0) || (this.index === this.items.length - 1 && offset < 0)) {
            offset *= 0.25;
          }
          this.track.style.transform = `translate3d(calc(-100% + ${offset}px), 0, 0)`;
        }
      }

      handlePointerEnd(event) {
        const point = this.pointers.get(event.pointerId);
        if (!point) return;

        const interaction = this.interaction;
        this.pointers.delete(event.pointerId);
        if (this.viewer.hasPointerCapture?.(event.pointerId)) this.viewer.releasePointerCapture(event.pointerId);

        if (interaction?.type === 'pinch') {
          if (this.pointers.size === 1 && this.zoom.scale > 1.01) {
            const remaining = Array.from(this.pointers.values())[0];
            this.interaction = {
              type: 'pan',
              pointerId: remaining.id,
              startX: remaining.x,
              startY: remaining.y,
              startZoomX: this.zoom.x,
              startZoomY: this.zoom.y,
              edgeOverflowX: 0,
              startedOnImage: true,
              moved: true,
            };
          } else {
            this.interaction = null;
            this.track.classList.remove('is-dragging');
          }
          return;
        }

        if (!interaction || interaction.pointerId !== event.pointerId) return;

        const deltaX = point.x - interaction.startX;
        const deltaY = point.y - interaction.startY;
        this.interaction = null;
        this.track.classList.remove('is-dragging');

        if (!interaction.moved) {
          this.settleTrack();
          if (interaction.startedOnImage) this.toggleZoom();
          return;
        }

        const horizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) >= 48;
        if (interaction.type === 'pan') {
          const passedHorizontalEdge =
            horizontalSwipe &&
            ((deltaX < 0 && interaction.edgeOverflowX <= -48) ||
              (deltaX > 0 && interaction.edgeOverflowX >= 48));
          if (passedHorizontalEdge) {
            this.navigate(deltaX < 0 ? 1 : -1);
            return;
          }

          this.applyZoom(true);
          return;
        }

        if (!horizontalSwipe) {
          this.settleTrack();
          return;
        }

        this.navigate(deltaX < 0 ? 1 : -1);
      }

      handlePointerCancel(event) {
        this.pointers.delete(event.pointerId);
        this.interaction = null;
        this.track.classList.remove('is-dragging');
        this.settleTrack();
      }

      startPinch() {
        const [first, second] = Array.from(this.pointers.values()).slice(0, 2);
        if (!first || !second) return;

        this.interaction = {
          type: 'pinch',
          startDistance: Math.max(1, Math.hypot(second.x - first.x, second.y - first.y)),
          startScale: this.zoom.scale,
          startMidpointX: (first.x + second.x) / 2,
          startMidpointY: (first.y + second.y) / 2,
          startZoomX: this.zoom.x,
          startZoomY: this.zoom.y,
        };
      }

      movePinch() {
        const [first, second] = Array.from(this.pointers.values()).slice(0, 2);
        const interaction = this.interaction;
        if (!first || !second || interaction?.type !== 'pinch') return;

        const distance = Math.hypot(second.x - first.x, second.y - first.y);
        const midpointX = (first.x + second.x) / 2;
        const midpointY = (first.y + second.y) / 2;
        this.zoom.scale = Math.min(4, Math.max(1, interaction.startScale * (distance / interaction.startDistance)));
        this.zoom.x = interaction.startZoomX + midpointX - interaction.startMidpointX;
        this.zoom.y = interaction.startZoomY + midpointY - interaction.startMidpointY;
        this.clampZoom();
        this.applyZoom();
      }

      toggleZoom() {
        const mobileViewport = window.matchMedia('(max-width: 749px)').matches;
        const targetScale = mobileViewport ? 3 : 2.25;
        if (this.zoom.scale > 1.01 && (mobileViewport || this.zoom.scale >= targetScale - 0.01)) {
          this.resetZoom(true);
          return;
        }

        this.zoom.scale = targetScale;
        this.zoom.x = 0;
        this.zoom.y = 0;
        this.applyZoom(true);
      }

      handleWheel(event) {
        if (!this.hasAttribute('open') || this.dataset.mode !== 'image') return;

        const multiplier = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerWidth : 1;
        const deltaX = event.deltaX * multiplier;
        const deltaY = event.deltaY * multiplier;
        const horizontalIntent = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) >= 1;

        if (event.ctrlKey) {
          this.handleTrackpadPinch(event, deltaY);
          return;
        }

        if (this.zoom.scale > 1.01) {
          event.preventDefault();
          const edgeOverflowX = this.setZoomPosition(this.zoom.x - deltaX, this.zoom.y - deltaY);
          this.applyZoom();
          if (horizontalIntent && Math.abs(edgeOverflowX) >= 0.5) {
            this.handleWheelNavigation(-edgeOverflowX);
          } else {
            this.wheelDistance = 0;
            this.wheelPendingDirection = 0;
          }
          return;
        }

        if (horizontalIntent && this.dataset.mode === 'image') {
          event.preventDefault();
          this.handleWheelNavigation(deltaX);
          return;
        }
      }

      handleTrackpadPinch(event, deltaY) {
        event.preventDefault();
        this.wheelDistance = 0;
        this.wheelPendingDirection = 0;

        const previousScale = this.zoom.scale;
        const nextScale = Math.min(4, Math.max(1, previousScale * Math.exp(-deltaY * 0.01)));
        this.setZoomAtPoint(nextScale, event.clientX, event.clientY);
      }

      handleGestureStart(event) {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        event.preventDefault();
        this.trackpadGestureScale = this.zoom.scale;
      }

      handleGestureChange(event) {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        event.preventDefault();
        const nextScale = Math.min(4, Math.max(1, this.trackpadGestureScale * event.scale));
        this.setZoomAtPoint(nextScale, event.clientX, event.clientY);
      }

      handleGestureEnd(event) {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        event.preventDefault();
        this.trackpadGestureScale = this.zoom.scale;
      }

      setZoomAtPoint(nextScale, clientX, clientY) {
        const previousScale = this.zoom.scale;
        if (nextScale <= 1.01) {
          this.resetZoom();
          return;
        }

        const viewerRect = this.viewer.getBoundingClientRect();
        const originX = Number.isFinite(clientX) ? clientX - viewerRect.left - viewerRect.width / 2 : 0;
        const originY = Number.isFinite(clientY) ? clientY - viewerRect.top - viewerRect.height / 2 : 0;
        const scaleRatio = nextScale / previousScale;
        this.zoom.x = originX - (originX - this.zoom.x) * scaleRatio;
        this.zoom.y = originY - (originY - this.zoom.y) * scaleRatio;
        this.zoom.scale = nextScale;
        this.clampZoom();
        this.applyZoom();
      }

      handleWheelNavigation(deltaX) {
        const now = performance.now();
        const direction = Math.sign(deltaX);
        const magnitude = Math.abs(deltaX);
        const quietPeriod = now - this.wheelLastEventTime > 90;
        const directionChanged = this.wheelLastDirection !== 0 && direction !== this.wheelLastDirection;
        const renewedForce = magnitude >= 12 && magnitude > this.wheelLastMagnitude * 1.75;

        if (quietPeriod || (!this.navigationTimer && (directionChanged || renewedForce))) {
          this.wheelGestureNavigated = false;
          this.wheelDistance = 0;
        }

        this.wheelLastDirection = direction;
        this.wheelLastEventTime = now;
        this.wheelLastMagnitude = magnitude;

        if (this.wheelGestureTimer) window.clearTimeout(this.wheelGestureTimer);
        this.wheelGestureTimer = window.setTimeout(() => {
          this.wheelGestureTimer = null;
          this.wheelGestureNavigated = false;
          this.wheelDistance = 0;
          this.wheelLastDirection = 0;
          this.wheelLastEventTime = 0;
          this.wheelLastMagnitude = 0;
        }, 100);

        if (this.wheelGestureNavigated) return;

        this.wheelDistance += deltaX;
        if (Math.abs(this.wheelDistance) < 48) return;

        const navigationDirection = this.wheelDistance > 0 ? 1 : -1;
        this.wheelDistance = 0;
        this.wheelGestureNavigated = true;
        if (this.navigationTimer) {
          this.wheelPendingDirection = navigationDirection;
          return;
        }
        this.navigate(navigationDirection);
      }

      get currentImage() {
        const currentPanel = this.panels[1];
        return (
          currentPanel?.querySelector('[data-product-modal-zoom-target]') ||
          currentPanel?.querySelector('[data-product-modal-image]:not([hidden])')
        );
      }

      clearLayeredPreviewPanels() {
        this.panels?.forEach((panel) => panel.querySelector('[data-product-modal-layered-preview]')?.remove());
      }

      setZoomPosition(x, y) {
        this.zoom.x = x;
        this.zoom.y = y;
        this.clampZoom();
        return x - this.zoom.x;
      }

      clampZoom() {
        const image = this.currentImage;
        if (!image || this.zoom.scale <= 1.01) {
          this.zoom.x = 0;
          this.zoom.y = 0;
          return;
        }

        const maximumX = (image.clientWidth * (this.zoom.scale - 1)) / 2;
        const maximumY = (image.clientHeight * (this.zoom.scale - 1)) / 2;
        this.zoom.x = Math.min(maximumX, Math.max(-maximumX, this.zoom.x));
        this.zoom.y = Math.min(maximumY, Math.max(-maximumY, this.zoom.y));
      }

      applyZoom(animate = false) {
        const image = this.currentImage;
        if (!image) return;

        image.classList.toggle('is-animating', animate);
        image.style.transform = `translate3d(${this.zoom.x}px, ${this.zoom.y}px, 0) scale(${this.zoom.scale})`;
        this.viewer.classList.toggle('is-zoomed', this.zoom.scale > 1.01);
        window.setTimeout(() => image?.classList.remove('is-animating'), 240);
      }

      resetZoom(animate = false) {
        const image = this.currentImage;
        this.zoom = { scale: 1, x: 0, y: 0 };
        this.viewer?.classList.remove('is-zoomed');
        if (!image) return;

        image.classList.toggle('is-animating', animate);
        image.style.transform = '';
        window.setTimeout(() => image?.classList.remove('is-animating'), 240);
      }

      resetViewer() {
        if (this.navigationTimer) window.clearTimeout(this.navigationTimer);
        if (this.settleTimer) window.clearTimeout(this.settleTimer);
        if (this.wheelGestureTimer) window.clearTimeout(this.wheelGestureTimer);
        this.navigationTimer = null;
        this.settleTimer = null;
        this.wheelGestureTimer = null;
        this.imageRequestId += 1;
        this.pointers.clear();
        this.interaction = null;
        this.wheelDistance = 0;
        this.wheelGestureNavigated = false;
        this.wheelLastDirection = 0;
        this.wheelLastEventTime = 0;
        this.wheelLastMagnitude = 0;
        this.wheelPendingDirection = 0;
        this.trackpadGestureScale = 1;
        this.resetZoom();
        this.clearLayeredPreviewPanels();
        this.track?.classList.remove('is-animating', 'is-dragging');
        if (this.track) this.track.style.transform = 'translate3d(-100%, 0, 0)';
        this.fallback?.querySelectorAll('.active').forEach((media) => media.classList.remove('active'));
      }
    }
  );
}
